import React, { useState } from 'react';
import { User, Phone, Lock, Save, Camera } from 'lucide-react';
import { authService } from '../../services/auth';
import { formatImageUrl } from '../../services/api';

const ProfilePage = () => {
  const currentUser = authService.getUser();

  const [name, setName] = useState(currentUser?.name || '');
  const [phone, setPhone] = useState(currentUser?.phone || '');
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileMessage, setProfileMessage] = useState('');
  const [avatar, setAvatar] = useState(currentUser?.avatar || '');
  const [uploadingAvatar, setUploadingAvatar] = useState(false);

  const handleAvatarChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploadingAvatar(true);
    try {
      const formData = new FormData();
      formData.append('avatar', file);

      const response = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/auth/avatar`, {
        method: 'POST',
        headers: authService.getAuthHeadersForFormData(),
        body: formData
      });
      const data = await response.json();

      if (!response.ok) throw new Error(data.error || 'Error al subir la foto');

      authService.setAuthData(data.token, data.user);
      setAvatar(data.user.avatar);
    } catch (error) {
      alert(error.message);
    } finally {
      setUploadingAvatar(false);
    }
  };

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState('');

  const handleProfileSubmit = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    setProfileMessage('');

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/auth/profile`, {
        method: 'PUT',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify({ name, phone })
      });
      const data = await response.json();

      if (!response.ok) throw new Error(data.error || 'Error al actualizar el perfil');

      authService.setAuthData(data.token, data.user);
      setProfileMessage('✅ Perfil actualizado correctamente');
    } catch (error) {
      setProfileMessage(`❌ ${error.message}`);
    } finally {
      setSavingProfile(false);
    }
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();
    setPasswordMessage('');

    if (newPassword !== confirmPassword) {
      setPasswordMessage('❌ Las contraseñas nuevas no coinciden');
      return;
    }

    setSavingPassword(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL || '/api'}/auth/change-password`, {
        method: 'POST',
        headers: authService.getAuthHeaders(),
        body: JSON.stringify({ currentPassword, newPassword })
      });
      const data = await response.json();

      if (!response.ok) throw new Error(data.error || 'Error al cambiar la contraseña');

      setPasswordMessage('✅ Contraseña actualizada correctamente');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      setPasswordMessage(`❌ ${error.message}`);
    } finally {
      setSavingPassword(false);
    }
  };

  return (
    <div className="max-w-2xl">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800">Mi Perfil</h1>
        <p className="text-gray-600">Editá tus datos y tu contraseña</p>
      </div>

      <div className="bg-white rounded-xl shadow p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Datos personales</h2>

        <div className="flex items-center gap-4 mb-6">
          <div className="relative">
            {avatar ? (
              <img
                src={formatImageUrl(avatar)}
                alt={name}
                className="w-16 h-16 rounded-full object-cover border border-gray-200"
              />
            ) : (
              <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                <span className="text-primary font-bold text-xl">{name?.charAt(0)?.toUpperCase() || 'A'}</span>
              </div>
            )}
            <label
              htmlFor="avatar-upload"
              className="absolute -bottom-1 -right-1 p-1.5 bg-blue-600 hover:bg-blue-700 rounded-full text-white cursor-pointer transition-colors"
              title="Cambiar foto"
            >
              <Camera className="w-3.5 h-3.5" />
            </label>
            <input
              id="avatar-upload"
              type="file"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              onChange={handleAvatarChange}
              className="hidden"
            />
          </div>
          <div>
            <p className="text-sm font-medium text-gray-800">Foto de perfil</p>
            <p className="text-xs text-gray-500">{uploadingAvatar ? 'Subiendo...' : 'Se muestra en las propiedades donde figures como contacto'}</p>
          </div>
        </div>

        <form onSubmit={handleProfileSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Nombre completo</label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Email</label>
            <input
              type="email"
              value={currentUser?.email || ''}
              disabled
              className="w-full px-4 py-3 border border-gray-200 bg-gray-50 text-gray-500 rounded-lg"
            />
            <p className="text-xs text-gray-500 mt-1">El email no se puede cambiar. Pedile a un admin si necesitás uno nuevo.</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Teléfono</label>
            <div className="relative">
              <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="+54 387 456-7890"
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">Se usa para autocompletar el contacto al crear una propiedad nueva.</p>
          </div>

          {profileMessage && <p className="text-sm">{profileMessage}</p>}

          <button
            type="submit"
            disabled={savingProfile}
            className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors disabled:opacity-60"
          >
            <Save className="w-5 h-5" />
            {savingProfile ? 'Guardando...' : 'Guardar cambios'}
          </button>
        </form>
      </div>

      <div className="bg-white rounded-xl shadow p-6">
        <h2 className="text-lg font-semibold text-gray-800 mb-4">Cambiar contraseña</h2>
        <form onSubmit={handlePasswordSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Contraseña actual</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Nueva contraseña</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Confirmar nueva contraseña</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                minLength={6}
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
              />
            </div>
          </div>

          {passwordMessage && <p className="text-sm">{passwordMessage}</p>}

          <button
            type="submit"
            disabled={savingPassword}
            className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors disabled:opacity-60"
          >
            <Save className="w-5 h-5" />
            {savingPassword ? 'Guardando...' : 'Cambiar contraseña'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ProfilePage;
